import { ReactElement, Dispatch, useState } from 'react';
import { FormControlLabel, Checkbox, Pagination, styled } from '@mui/material';
import { variables, IDeaEditMode } from '../common';
import {
  allDeaNumSelected,
  RequestAction,
  RegistrationNo,
  MAX_REQUEST_DEAINFOs,
  selectedDeaInfoSize,
} from '.';
import { not, pathOr } from 'rambda';
import { map as lmap, filter as lfilter, reject as lreject } from 'lodash/fp';
import { flow } from 'fp-ts/function';

export function RegistrationNos({
  deaNos,
  reqType,
  dispatchRequest,

  tooltipValue,
  toggleTooltip,
  offTooltip,
  feedback,
}: {
  deaNos: IDeaEditMode[];
  reqType: string;
  dispatchRequest: Dispatch<RequestAction>;

  tooltipValue: (tooltip: string) => boolean;
  toggleTooltip: (tooltip: string) => void;
  offTooltip: (tooltip: string) => void;
  feedback: string;
}) {
  const select = ({ checked, key }: { checked: boolean; key: string }) =>
    dispatchRequest({ type: 'SelectDeaInfo', payload: { checked, key } });

  const selectAll = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) =>
    dispatchRequest({
      type: 'SelectAllDeaInfo',
      payload: {
        checked,
        keys: checked
          ? flow(
              (a: IDeaEditMode[]) =>
                a.slice((page - 1) * pageSize, page * pageSize),
              lreject({ selected: true }),
              lmap(pathOr('', 'key'))
            )(deaNos)
          : flow(
              (a: IDeaEditMode[]) =>
                a.slice((page - 1) * pageSize, page * pageSize),
              lmap(pathOr('', 'key'))
            )(deaNos),
      },
    });

  const [page, setPage] = useState(1);
  const pageSize = deaNos.length > 1000 ? 1000 : 100;

  return (
    <Form autoComplete="off">
      <DeaNumListSection aria-labelledby="dea-reg-num-list-label">
        {deaNos.length > 0 && (
          <DeaNumListHeader>
            <span id="dea-reg-num-list-label">
              DEA Registration Number List
            </span>

            <FormControlLabel
              checked={flow(
                (a: IDeaEditMode[]) =>
                  a.slice((page - 1) * pageSize, page * pageSize),
                allDeaNumSelected
              )(deaNos)}
              disabled={
                selectedDeaInfoSize(deaNos) >= MAX_REQUEST_DEAINFOs &&
                flow(
                  (a: IDeaEditMode[]) =>
                    a.slice((page - 1) * pageSize, page * pageSize),
                  allDeaNumSelected,
                  not
                )(deaNos)
              }
              control={<Checkbox onChange={selectAll} color="primary" />}
              label="Select by Page"
              labelPlacement="end"
            />

            <h2 style={{ color: variables.brandInfo }}>
              <span>
                {deaNos.length} certificates eligible for{' '}
                {reqType === 'RENEWAL' ? 'renewal' : 'revoke'}.{' '}
                <span
                  style={{
                    color:
                      flow(
                        lfilter({ selected: true }),
                        a => a.length
                      )(deaNos) === MAX_REQUEST_DEAINFOs
                        ? variables.brandDanger
                        : void 0,
                  }}>
                  {flow(lfilter({ selected: true }), a => a.length)(deaNos)} DEA
                  Registrations selected
                </span>{' '}
                (max {MAX_REQUEST_DEAINFOs} per request).
              </span>
            </h2>
          </DeaNumListHeader>
        )}
        {deaNos.length > pageSize && (
          <Paginatio
            count={Math.ceil(deaNos.length / pageSize)}
            page={page}
            onChange={(z, page) => setPage(page)}
            shape="rounded"
          />
        )}

        <Wrapper>
          {flow(
            (a: IDeaEditMode[]) =>
              a.slice((page - 1) * pageSize, page * pageSize),
            lmap<IDeaEditMode, ReactElement>(a => {
              switch (a.editMode) {
                case 'Select':
                  return (
                    <RegistrationNo
                      key={a.key}
                      item={a}
                      select={select}
                      tooltipValue={tooltipValue}
                      toggleTooltip={toggleTooltip}
                      offTooltip={offTooltip}
                      feedback={feedback}
                    />
                  );

                default:
                  return <>NA</>;
              }
            })
          )(deaNos)}
        </Wrapper>

        {deaNos.length > pageSize && (
          <Paginatio
            count={Math.ceil(deaNos.length / pageSize)}
            page={page}
            onChange={(z, page) => setPage(page)}
            shape="rounded"
          />
        )}

        <div className="empty-state">
          {reqType === 'RENEWAL' && (
            <h4>
              You do not have any DEA registration number expiring in next 45
              days.
            </h4>
          )}
          {reqType === 'REVOKE' && <h4>Empty DEA Registration Number List</h4>}
        </div>
      </DeaNumListSection>
    </Form>
  );
}

const Form = styled('form')(({ theme }) => ({
  ...variables.childrenMarginTop2x,
}));

const DeaNumListSection = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '1rem',
  },
}));

const DeaNumListHeader = styled('h4')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  '&>*+*': {
    marginLeft: '1rem !important',
  },
}));

const Wrapper = styled('ul')(({ theme }) => ({
  display: 'grid',

  gridTemplateColumns: '1fr',
  '@media (min-width: 60em)': {
    gridTemplateColumns: 'repeat(2, 35rem)',
  },
  '@media (min-width: 90em)': {
    gridTemplateColumns: 'repeat(3, 35rem)',
  },
  '@media (min-width: 120em)': {
    gridTemplateColumns: 'repeat(4, 35rem)',
  },

  gap: `1rem`,
}));

const Paginatio = styled(Pagination)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
}));
